/**
 * Report Generator Modal
 *
 * Modal for configuring and generating PDF project reports.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';

interface Flight {
  id: string;
  display_id: string;
  name: string;
  status: string;
  start_date: string;
  end_date: string;
}

interface ProjectInfo {
  project: {
    id: string;
    display_id: string;
    name: string;
  };
  flights: Flight[];
  stats: {
    totalPosts: number;
    publishedPosts: number;
    scheduledPosts: number;
    draftPosts: number;
    firstPostDate: string | null;
    lastPostDate: string | null;
    totalFlights: number;
    hasPillars: boolean;
  };
}

interface ReportOptions {
  language: 'de' | 'en';
  include_flights: boolean;
  include_posts: boolean;
  include_post_images: boolean;
  image_size: 'thumbnail' | 'medium' | 'large';
  post_layout: 'compact' | 'detailed';
  include_pillars: boolean;
  include_editorial: boolean;
  include_kpis: boolean;
  include_calendar_view: boolean;
  include_list_view: boolean;
  include_flight_stats: boolean;
  include_marketing: boolean;
  date_range?: {
    from: string;
    to: string;
  };
  flight_ids?: string[];
  status_filter?: string[];
}

interface ReportGeneratorModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ReportGeneratorModal({ projectId, isOpen, onClose }: ReportGeneratorModalProps) {
  const { t } = useTranslation(['reports', 'common']);
  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Report options
  const [language, setLanguage] = useState<'de' | 'en'>('de');
  const [includeFlights, setIncludeFlights] = useState(true);
  const [includePosts, setIncludePosts] = useState(true);
  const [includePostImages, setIncludePostImages] = useState(true);
  const [imageSize, setImageSize] = useState<'thumbnail' | 'medium' | 'large'>('medium');
  const [postLayout, setPostLayout] = useState<'compact' | 'detailed'>('compact');
  const [includePillars, setIncludePillars] = useState(true);
  const [includeCalendarView, setIncludeCalendarView] = useState(true);
  const [includeFlightStats, setIncludeFlightStats] = useState(true);
  const [includeMarketing, setIncludeMarketing] = useState(true);
  const [useDateRange, setUseDateRange] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedFlightIds, setSelectedFlightIds] = useState<string[]>([]);
  const [filterAllFlights, setFilterAllFlights] = useState(true);

  // Fetch project info on open
  useEffect(() => {
    if (isOpen && projectId) {
      setIsLoading(true);
      setError(null);
      api.get<ProjectInfo>(`/reports/project-info/${projectId}`)
        .then(data => {
          setProjectInfo(data);
          // Set default date range from post dates
          if (data.stats.firstPostDate) {
            setDateFrom(data.stats.firstPostDate.split('T')[0]);
          }
          if (data.stats.lastPostDate) {
            setDateTo(data.stats.lastPostDate.split('T')[0]);
          }
        })
        .catch(err => {
          setError(err instanceof Error ? err.message : t('reports:error.loadingProject'));
        })
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, projectId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFlightIds([]);
      setFilterAllFlights(true);
      setUseDateRange(false);
    }
  }, [isOpen]);

  const handleFlightToggle = (flightId: string) => {
    setSelectedFlightIds(prev =>
      prev.includes(flightId)
        ? prev.filter(id => id !== flightId)
        : [...prev, flightId]
    );
  };

  const handleGeneratePdf = async () => {
    setIsGenerating(true);
    setError(null);

    const options: ReportOptions = {
      language,
      include_flights: includeFlights,
      include_posts: includePosts,
      include_post_images: includePostImages,
      image_size: imageSize,
      post_layout: postLayout,
      include_pillars: includePillars,
      include_editorial: false,
      include_kpis: false,
      include_calendar_view: includeCalendarView,
      include_list_view: false,
      include_flight_stats: includeFlightStats,
      include_marketing: includeMarketing,
    };

    if (useDateRange && dateFrom && dateTo) {
      options.date_range = { from: dateFrom, to: dateTo };
    }

    if (!filterAllFlights && selectedFlightIds.length > 0) {
      options.flight_ids = selectedFlightIds;
    }

    try {
      const { blob, filename } = await api.postBlob('/reports/project-pdf', {
        project_id: projectId,
        options,
      });

      // Try to use the File System Access API for "Save As" dialog
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await (window as unknown as { showSaveFilePicker: (opts: unknown) => Promise<FileSystemFileHandle> }).showSaveFilePicker({
            suggestedName: filename || 'report.pdf',
            types: [{
              description: 'PDF Document',
              accept: { 'application/pdf': ['.pdf'] },
            }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          onClose();
          return;
        } catch (e) {
          // User cancelled or API not supported, fall back to download
          if ((e as Error).name === 'AbortError') {
            setIsGenerating(false);
            return;
          }
        }
      }

      // Fallback: trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'report.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common:error.unknown'));
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('reports:createPdfReport')}</h2>
              {projectInfo && (
                <p className="text-sm text-gray-500 mt-0.5">{projectInfo.project.name}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-5 overflow-y-auto max-h-[calc(90vh-180px)]">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : error && !projectInfo ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
              </div>
            ) : projectInfo ? (
              <div className="space-y-6">
                {/* Stats Overview */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900">{projectInfo.stats.totalPosts}</div>
                    <div className="text-xs text-gray-500">{t('reports:stats.posts')}</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-600">{projectInfo.stats.publishedPosts}</div>
                    <div className="text-xs text-gray-500">{t('reports:stats.published')}</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600">{projectInfo.stats.scheduledPosts}</div>
                    <div className="text-xs text-gray-500">{t('reports:stats.scheduled')}</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-purple-600">{projectInfo.stats.totalFlights}</div>
                    <div className="text-xs text-gray-500">{t('reports:stats.campaigns')}</div>
                  </div>
                </div>

                {/* Language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reports:language')}</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setLanguage('de')}
                      className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
                        language === 'de'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {t('common:languages.german')}
                    </button>
                    <button
                      onClick={() => setLanguage('en')}
                      className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
                        language === 'en'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {t('common:languages.english')}
                    </button>
                  </div>
                </div>

                {/* Date Range */}
                <div>
                  <label className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={useDateRange}
                      onChange={(e) => setUseDateRange(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">{t('reports:dateRange.restrict')}</span>
                  </label>
                  {useDateRange && (
                    <div className="flex gap-3 mt-2">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">{t('reports:dateRange.from')}</label>
                        <input
                          type="date"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">{t('reports:dateRange.to')}</label>
                        <input
                          type="date"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Content Sections */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reports:content.title')}</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeFlights}
                        onChange={(e) => setIncludeFlights(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{t('reports:content.flights')}</span>
                        <p className="text-xs text-gray-500">{t('reports:content.flightsDesc')}</p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includePosts}
                        onChange={(e) => setIncludePosts(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{t('reports:content.posts')}</span>
                        <p className="text-xs text-gray-500">{t('reports:content.postsDesc')}</p>
                      </div>
                    </label>

                    {includePosts && (
                      <div className="ml-8 space-y-3 py-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={includePostImages}
                            onChange={(e) => setIncludePostImages(e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{t('reports:content.embedImages')}</span>
                        </label>

                        {includePostImages && (
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">{t('reports:content.imageSize')}</label>
                            <div className="flex gap-2">
                              {(['thumbnail', 'medium', 'large'] as const).map((size) => (
                                <button
                                  key={size}
                                  onClick={() => setImageSize(size)}
                                  className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                                    imageSize === size
                                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  {size === 'thumbnail' ? t('reports:content.sizes.small') : size === 'medium' ? t('reports:content.sizes.medium') : t('reports:content.sizes.large')}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-xs text-gray-500 mb-1">{t('reports:content.postLayout')}</label>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setPostLayout('compact')}
                              className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                                postLayout === 'compact'
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <span className="block font-medium">{t('reports:content.layouts.compact')}</span>
                              <span className="text-xs opacity-70">{t('reports:content.layouts.compactDesc')}</span>
                            </button>
                            <button
                              onClick={() => setPostLayout('detailed')}
                              className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                                postLayout === 'detailed'
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <span className="block font-medium">{t('reports:content.layouts.detailed')}</span>
                              <span className="text-xs opacity-70">{t('reports:content.layouts.detailedDesc')}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {projectInfo.stats.hasPillars && (
                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includePillars}
                          onChange={(e) => setIncludePillars(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">{t('reports:content.pillars')}</span>
                          <p className="text-xs text-gray-500">{t('reports:content.pillarsDesc')}</p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* View Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('reports:views.title')}</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeCalendarView}
                        onChange={(e) => setIncludeCalendarView(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{t('reports:views.calendar')}</span>
                        <p className="text-xs text-gray-500">{t('reports:views.calendarDesc')}</p>
                      </div>
                    </label>


                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeFlightStats}
                        onChange={(e) => setIncludeFlightStats(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{t('reports:views.flightStats')}</span>
                        <p className="text-xs text-gray-500">{t('reports:views.flightStatsDesc')}</p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeMarketing}
                        onChange={(e) => setIncludeMarketing(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{t('reports:views.marketing')}</span>
                        <p className="text-xs text-gray-500">{t('reports:views.marketingDesc')}</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Flight Filter */}
                {projectInfo.flights.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reports:flightFilter.title')}
                      <span className="text-xs text-gray-400 ml-2">({t('reports:flightFilter.available', { count: projectInfo.flights.length })})</span>
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          checked={filterAllFlights}
                          onChange={() => {
                            setFilterAllFlights(true);
                            setSelectedFlightIds([]);
                          }}
                          className="border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{t('reports:flightFilter.includeAll')}</span>
                      </label>
                      <label className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          checked={!filterAllFlights}
                          onChange={() => setFilterAllFlights(false)}
                          className="border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{t('reports:flightFilter.selectedOnly')}</span>
                      </label>

                      {!filterAllFlights && (
                        <div className="mt-2 space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                          {selectedFlightIds.length === 0 && (
                            <p className="text-xs text-amber-600 px-2 py-1">{t('reports:flightFilter.selectAtLeastOne')}</p>
                          )}
                          {projectInfo.flights.map((flight) => {
                            const isSelected = selectedFlightIds.includes(flight.id);
                            const startDate = new Date(flight.start_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
                            const endDate = new Date(flight.end_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                            return (
                              <label
                                key={flight.id}
                                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                  isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-white border border-transparent'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleFlightToggle(flight.id)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium text-gray-900 block truncate">{flight.name}</span>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span>{flight.display_id}</span>
                                    <span>•</span>
                                    <span>{startDate} - {endDate}</span>
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-5 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={handleGeneratePdf}
              disabled={isGenerating || isLoading || !projectInfo}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>{t('reports:generating')}</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>{t('reports:createPdf')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportGeneratorModal;
